/**
 * Extrato bancário na aba Conciliação: importa os movimentos da conta pelo
 * banco (Sicoob: API Conta Corrente) e cada um vira lançamento pago, casa
 * com um existente ou é ignorado. Quem já foi classificado uma vez para a
 * mesma contraparte vem com a categoria sugerida.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import { apiFetch, mensagemDeErro } from '@/lib/api';
import { formatBRL } from '@/lib/utils';

type TxStatus = 'PENDING' | 'CLASSIFIED' | 'MATCHED' | 'IGNORED';
type TxTipo = 'DEBIT' | 'CREDIT';

interface Categoria {
  id: string;
  codigo: string;
  nome: string;
  kind: 'INCOME' | 'EXPENSE' | 'TRIBUTE' | 'DISTRIBUTION';
  isActive?: boolean;
}

interface Conta {
  id: string;
  name: string;
  providerName: string;
}

interface Movimento {
  id: string;
  tipo: TxTipo;
  valor: string | number;
  data: string;
  dataLote?: string | null;
  descricao: string;
  numeroDocumento?: string | null;
  complemento?: string | null;
  status: TxStatus;
  billingChargeId?: string | null;
  financialEntry?: {
    id: string;
    referencia: string;
    status: string;
    category?: { id: string; nome: string; codigo: string } | null;
    partner?: { id: string; fullName: string } | null;
  } | null;
  billingCharge?: { providerChargeId?: string | null } | null;
  sugestaoCategoria?: { id: string; nome: string; codigo: string } | null;
  sugestaoSocio?: { id: string; fullName: string; isActive: boolean } | null;
}

interface Saldo {
  available: number;
  blocked?: number;
  limit?: number;
}

const STATUS: { value: TxStatus; label: string }[] = [
  { value: 'PENDING', label: 'Pendentes' },
  { value: 'CLASSIFIED', label: 'Lançados' },
  { value: 'MATCHED', label: 'Casados' },
  { value: 'IGNORED', label: 'Ignorados' },
];

const KINDS_POR_TIPO: Record<TxTipo, Categoria['kind'][]> = {
  DEBIT: ['EXPENSE', 'TRIBUTE', 'DISTRIBUTION'],
  CREDIT: ['INCOME'],
};

function mesAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "AAAA-MM" -> primeiro e ultimo dia (ate hoje, se for o mes corrente). */
function periodoDoMes(mes: string): { from: string; to: string } {
  const [a, m] = mes.split('-').map(Number);
  const ultimo = new Date(a!, m!, 0).getDate();
  const hoje = new Date();
  const ehMesAtual = hoje.getFullYear() === a && hoje.getMonth() + 1 === m;
  const diaFinal = ehMesAtual ? hoje.getDate() : ultimo;
  return {
    from: `${mes}-01`,
    to: `${mes}-${String(diaFinal).padStart(2, '0')}`,
  };
}

function fmtDataHora(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Tira o ruído do complemento do banco: "Pagamento Pix|@04.679.527 0001-45|@" -> "Pagamento Pix · 04.679.527 0001-45". */
function complementoLimpo(c?: string | null): string {
  if (!c) return '';
  return c
    .split('|')
    .map((p) => p.replace(/^@/, '').trim())
    .filter(Boolean)
    .join(' · ');
}

export function ExtratoBancario({ canManage, onChanged }: { canManage: boolean; onChanged: () => void }) {
  const qc = useQueryClient();
  const [accountId, setAccountId] = useState('');
  const [mes, setMes] = useState(mesAtual());
  const [status, setStatus] = useState<TxStatus>('PENDING');
  const [categoriaPorLinha, setCategoriaPorLinha] = useState<Record<string, string>>({});
  // Socio desmarcado explicitamente numa linha (a sugestao vem marcada).
  const [semSocio, setSemSocio] = useState<Record<string, boolean>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const contas = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => apiFetch<Conta[]>('/bank-accounts'),
  });
  const contasComExtrato = (contas.data ?? []).filter((c) => ['SICOOB', 'ITAU'].includes(c.providerName));
  const contaAtiva = accountId || contasComExtrato[0]?.id || '';

  const categorias = useQuery({
    queryKey: ['categories', 'ativas'],
    queryFn: () => apiFetch<Categoria[]>('/categories?isActive=true'),
  });

  const saldo = useQuery({
    queryKey: ['bank-statement', 'saldo', contaAtiva],
    queryFn: () => apiFetch<Saldo>(`/bank-statement/accounts/${contaAtiva}/balance`),
    enabled: !!contaAtiva,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const { from, to } = periodoDoMes(mes);
  const movimentos = useQuery({
    queryKey: ['bank-statement', 'tx', { contaAtiva, status, from, to }],
    queryFn: () =>
      apiFetch<Movimento[]>(
        `/bank-statement/transactions?${new URLSearchParams({ accountId: contaAtiva, status, from, to })}`,
      ),
    enabled: !!contaAtiva,
  });

  function recarregar(msg?: string) {
    setErro(null);
    if (msg) setAviso(msg);
    void qc.invalidateQueries({ queryKey: ['bank-statement'] });
    onChanged();
  }
  const falhou = (e: unknown) => {
    setAviso(null);
    setErro(mensagemDeErro(e));
  };

  const importar = useMutation({
    mutationFn: () =>
      apiFetch<{ total: number; novas: number; jaImportadas: number; casadas: number }>(
        `/bank-statement/accounts/${contaAtiva}/import`,
        { method: 'POST', body: JSON.stringify({ from, to }) },
      ),
    onSuccess: (r) =>
      recarregar(
        `Banco devolveu ${r.total} movimento(s): ${r.novas} novo(s), ${r.jaImportadas} já estavam aqui, ${r.casadas} casado(s) com boleto.`,
      ),
    onError: falhou,
  });

  const lancar = useMutation({
    mutationFn: ({ id, categoryId, partnerId }: { id: string; categoryId: string; partnerId?: string }) =>
      apiFetch(`/bank-statement/transactions/${id}/classify`, {
        method: 'POST',
        body: JSON.stringify({ categoryId, ...(partnerId ? { partnerId } : {}) }),
      }),
    onSuccess: () => recarregar('Lançado no Financeiro como pago.'),
    onError: falhou,
  });

  const ignorar = useMutation({
    mutationFn: (id: string) => apiFetch(`/bank-statement/transactions/${id}/ignore`, { method: 'POST' }),
    onSuccess: () => recarregar(),
    onError: falhou,
  });

  const desfazer = useMutation({
    mutationFn: (id: string) => apiFetch(`/bank-statement/transactions/${id}/undo`, { method: 'POST' }),
    onSuccess: () => recarregar('Desfeito — o movimento voltou para pendente.'),
    onError: falhou,
  });

  const ocupado = importar.isPending || lancar.isPending || ignorar.isPending || desfazer.isPending;
  const linhas = movimentos.data ?? [];
  const totalDebitos = linhas.filter((l) => l.tipo === 'DEBIT').reduce((s, l) => s + Number(l.valor), 0);
  const totalCreditos = linhas.filter((l) => l.tipo === 'CREDIT').reduce((s, l) => s + Number(l.valor), 0);

  if (!contas.isLoading && contasComExtrato.length === 0) {
    return (
      <section className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
        Nenhuma conta com extrato por API. Cadastre uma conta Sicoob ou Itaú em <b>Contas Bancárias</b>.
      </section>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h2 className="text-base font-medium">Extrato bancário</h2>
          <p className="text-xs text-muted-foreground">
            Traga os movimentos do banco e transforme cada um em lançamento. Nada entra duas vezes.
          </p>
        </div>
        <label className="ml-auto block space-y-1">
          <span className="text-xs font-medium">Conta</span>
          <select
            className="input"
            value={contaAtiva}
            onChange={(e) => setAccountId(e.target.value)}
          >
            {contasComExtrato.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium">Mês</span>
          <input type="month" className="input" value={mes} onChange={(e) => setMes(e.target.value)} />
        </label>
        {canManage && (
          <button
            type="button"
            disabled={ocupado || !contaAtiva}
            onClick={() => importar.mutate()}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> {importar.isPending ? 'Importando…' : 'Importar do banco'}
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {saldo.data && (
          <span>
            Saldo no banco agora: <b className="text-foreground">{formatBRL(saldo.data.available)}</b>
            {saldo.data.limit ? ` (limite ${formatBRL(saldo.data.limit)})` : ''}
          </span>
        )}
        {saldo.isError && <span>Saldo indisponível: {mensagemDeErro(saldo.error)}</span>}
        <span className="ml-auto">
          Neste filtro: <b className="text-destructive">−{formatBRL(totalDebitos)}</b> ·{' '}
          <b className="text-emerald-700">+{formatBRL(totalCreditos)}</b>
        </span>
      </div>

      {erro && (
        <p role="alert" className="rounded-md bg-destructive/10 p-2 text-sm text-destructive">
          {erro}
        </p>
      )}
      {aviso && <p className="rounded-md bg-emerald-500/10 p-2 text-sm text-emerald-700">{aviso}</p>}

      <div className="flex rounded-md border border-border p-0.5 text-sm">
        {STATUS.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => setStatus(s.value)}
            className={`rounded px-3 py-1.5 ${status === s.value ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {movimentos.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {movimentos.data && linhas.length === 0 && (
        <p className="p-4 text-center text-sm text-muted-foreground">
          {status === 'PENDING'
            ? 'Nada pendente neste mês. Clique em "Importar do banco" para buscar movimentos novos.'
            : 'Nada aqui neste mês.'}
        </p>
      )}

      {linhas.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Data</th>
                <th className="px-3 py-2">Movimento</th>
                <th className="px-3 py-2 text-right">Valor</th>
                <th className="px-3 py-2">{status === 'PENDING' ? 'Categoria' : 'Lançamento'}</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => {
                const kinds = KINDS_POR_TIPO[l.tipo];
                const opcoes = (categorias.data ?? []).filter((c) => kinds.includes(c.kind));
                const escolhida = categoriaPorLinha[l.id] ?? l.sugestaoCategoria?.id ?? '';
                return (
                  <tr key={l.id} className="border-t border-border align-top">
                    <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtDataHora(l.data)}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs font-medium">{l.descricao}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {complementoLimpo(l.complemento)}
                        {l.numeroDocumento && l.numeroDocumento !== 'Pix' ? ` · doc ${l.numeroDocumento}` : ''}
                      </div>
                      {l.status === 'PENDING' && l.sugestaoSocio && (
                        <label className="mt-1 flex items-center gap-1 text-[11px]">
                          <input
                            type="checkbox"
                            checked={!semSocio[l.id]}
                            onChange={(e) => setSemSocio((m) => ({ ...m, [l.id]: !e.target.checked }))}
                          />
                          sócio <b>{l.sugestaoSocio.fullName}</b> (CPF confere)
                        </label>
                      )}
                      {l.status !== 'PENDING' && l.financialEntry?.partner && (
                        <div className="text-[11px] text-muted-foreground">sócio {l.financialEntry.partner.fullName}</div>
                      )}
                    </td>
                    <td
                      className={`whitespace-nowrap px-3 py-2 text-right font-medium ${
                        l.tipo === 'DEBIT' ? 'text-destructive' : 'text-emerald-700'
                      }`}
                    >
                      {l.tipo === 'DEBIT' ? '−' : '+'}
                      {formatBRL(Number(l.valor))}
                    </td>
                    <td className="px-3 py-2">
                      {l.status === 'PENDING' ? (
                        <div className="space-y-1">
                          <select
                            className="input w-full min-w-[220px] text-xs"
                            value={escolhida}
                            disabled={!canManage}
                            onChange={(e) =>
                              setCategoriaPorLinha((m) => ({ ...m, [l.id]: e.target.value }))
                            }
                          >
                            <option value="">— escolha —</option>
                            {opcoes.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.codigo} {c.nome}
                              </option>
                            ))}
                          </select>
                          {l.sugestaoCategoria && !categoriaPorLinha[l.id] && (
                            <div className="text-[11px] text-muted-foreground">
                              sugerida pelo histórico desta contraparte
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs">
                          {l.financialEntry ? (
                            <>
                              <div>{l.financialEntry.referencia}</div>
                              <div className="text-[11px] text-muted-foreground">
                                {l.financialEntry.category
                                  ? `${l.financialEntry.category.codigo} ${l.financialEntry.category.nome}`
                                  : l.billingCharge
                                    ? `boleto ${l.billingCharge.providerChargeId ?? ''}`
                                    : ''}
                              </div>
                            </>
                          ) : (
                            <span className="text-muted-foreground">
                              {l.status === 'IGNORED' ? 'ignorado' : '—'}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-right">
                      {canManage && l.status === 'PENDING' && (
                        <>
                          <button
                            type="button"
                            disabled={ocupado || !escolhida}
                            onClick={() =>
                              lancar.mutate({
                                id: l.id,
                                categoryId: escolhida,
                                partnerId: l.sugestaoSocio && !semSocio[l.id] ? l.sugestaoSocio.id : undefined,
                              })
                            }
                            className="rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                          >
                            Lançar
                          </button>
                          <button
                            type="button"
                            disabled={ocupado}
                            onClick={() => ignorar.mutate(l.id)}
                            className="ml-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-accent disabled:opacity-50"
                          >
                            Ignorar
                          </button>
                        </>
                      )}
                      {canManage && l.status !== 'PENDING' && !l.billingChargeId && (
                        <button
                          type="button"
                          disabled={ocupado}
                          onClick={() => desfazer.mutate(l.id)}
                          data-dica="Desfazer — volta para pendente"
                          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent disabled:opacity-50"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
